/**
 * useCalcSoftPaywall
 *
 * Tiny hook used by all 11 public calculator pages to wire up Cohort 1's
 * soft-paywall gate without duplicating state machinery.
 *
 * Usage at the calc page:
 *
 *   const { gated, gatedResult, tryShowResult, unlock } = useCalcSoftPaywall();
 *   const onSubmit = (e) => {
 *     e.preventDefault();
 *     const out = computeResult(...);
 *     if (!tryShowResult(out, setResult)) {
 *       // gate is now armed, render <SoftPaywallGate />
 *     }
 *   };
 *   // in JSX after the form:
 *   {gated && <SoftPaywallGate toolSlug={TOOL_SLUG} onUnlock={() => unlock(setResult)} />}
 */
import { useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import {
    shouldGateCalcRun,
    incrementCalcRun,
} from "@/lib/softPaywall";

export default function useCalcSoftPaywall() {
    const { user } = useAuth();
    const [gated, setGated] = useState(false);
    const [pending, setPending] = useState(null);

    /**
     * Decides whether to actually publish the calc result. If the gate is
     * not armed, increments the run counter and forwards the result to the
     * caller's setter. If gated, stashes the result in local state until
     * the visitor unlocks via the gate form.
     *
     * Returns true if the result was published immediately, false if the
     * caller needs to render the gate.
     */
    const tryShowResult = useCallback(
        (out, setResult) => {
            if (shouldGateCalcRun({ isAuthenticated: !!user })) {
                setPending({ out, setResult });
                setGated(true);
                return false;
            }
            incrementCalcRun();
            setResult(out);
            return true;
        },
        [user],
    );

    /** Called by SoftPaywallGate after a successful unlock. */
    const unlock = useCallback(() => {
        if (pending) {
            incrementCalcRun();
            pending.setResult(pending.out);
        }
        setPending(null);
        setGated(false);
    }, [pending]);

    return { gated, tryShowResult, unlock };
}
